function normalizeBaseUrl(value) {
  return String(value ?? "").trim().replace(/\/+$/, "");
}

function isLoopbackHost(hostname) {
  return hostname === "127.0.0.1" || hostname === "localhost";
}

function harmonizeLoopbackBaseUrl(value, locationLike = globalThis.location) {
  const normalized = normalizeBaseUrl(value);
  if (!normalized) {
    return "";
  }
  try {
    const url = new URL(normalized, locationLike?.href);
    if (
      locationLike
      && isLoopbackHost(url.hostname)
      && isLoopbackHost(locationLike.hostname)
      && url.hostname !== locationLike.hostname
    ) {
      url.hostname = locationLike.hostname;
    }
    return normalizeBaseUrl(url.toString());
  } catch (_) {
    return normalized;
  }
}

function locationBaseUrl(locationLike = globalThis.location) {
  if (locationLike?.origin) {
    return normalizeBaseUrl(locationLike.origin);
  }
  if (!locationLike?.hostname) {
    return "";
  }
  const protocol = locationLike.protocol === "http:" ? "http:" : "https:";
  return normalizeBaseUrl(`${protocol}//${locationLike.hostname}`);
}

function deriveSiblingBaseUrl(baseUrl, subdomain) {
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  const normalizedSubdomain = String(subdomain ?? "").trim().replace(/\.+$/, "");
  if (!normalizedBaseUrl || !normalizedSubdomain) {
    return "";
  }
  try {
    const url = new URL(normalizedBaseUrl);
    if (!url.hostname || isLoopbackHost(url.hostname)) {
      return "";
    }
    url.hostname = `${normalizedSubdomain}.${url.hostname}`;
    url.pathname = "";
    url.search = "";
    url.hash = "";
    return normalizeBaseUrl(url.toString());
  } catch (_) {
    return "";
  }
}

function resolveServiceBaseUrl({
  locationLike,
  explicitBaseUrl,
  configuredBaseUrl,
  subdomain,
  loopbackPort,
  productionFallback,
}) {
  const configured = [explicitBaseUrl, configuredBaseUrl]
    .map((value) => harmonizeLoopbackBaseUrl(value, locationLike))
    .find(Boolean);
  if (configured) {
    return configured;
  }
  if (isLoopbackHost(locationLike?.hostname)) {
    const protocol = locationLike?.protocol === "https:" ? "https:" : "http:";
    return `${protocol}//${locationLike.hostname}:${loopbackPort}`;
  }
  return deriveSiblingBaseUrl(locationBaseUrl(locationLike), subdomain) || productionFallback;
}

export function resolveApiBaseUrl({
  locationLike = globalThis.location,
  explicitBaseUrl = globalThis.window?.__fishystuffApiBaseUrl,
  runtimeConfig = globalThis.window?.__fishystuffRuntimeConfig,
} = {}) {
  return resolveServiceBaseUrl({
    locationLike,
    explicitBaseUrl,
    configuredBaseUrl: runtimeConfig?.apiBaseUrl,
    subdomain: "api",
    loopbackPort: 8080,
    productionFallback: "https://api.fishystuff.fish",
  });
}

export function resolveCdnBaseUrl(
  {
    locationLike = globalThis.location,
    explicitBaseUrl = globalThis.window?.__fishystuffCdnBaseUrl,
    runtimeConfig = globalThis.window?.__fishystuffRuntimeConfig,
  } = {},
) {
  return resolveServiceBaseUrl({
    locationLike,
    explicitBaseUrl,
    configuredBaseUrl: runtimeConfig?.cdnBaseUrl,
    subdomain: "cdn",
    loopbackPort: 4040,
    productionFallback: "https://cdn.fishystuff.fish",
  });
}

export function resolveMapRuntimeManifestUrl(options = {}) {
  return new URL("runtime-manifest.json", `${resolveCdnBaseUrl(options)}/map/`).toString();
}
